/**
 * The /app/l/:locationId/<page> routes, one per owner page, and on Fly's own
 * hosts their clean twins /app/:slug/<page> (docs/agent-clean-addresses-plan.md,
 * PR 1; Shardul, 22 and 23 Oct 26).
 *
 * Same components as the token routes in App.tsx, same lazy chunks, wrapped in
 * AppLocationShell so the person is signed in before an op is called. Kept in
 * one place so App.tsx gains a single line and the account area being built on
 * branch app-home-locations can absorb the block whole.
 *
 * The page list lives in pages.ts as data, and a guard test holds it against
 * the /agent routes so a new owner page cannot land with only one address.
 *
 * On Fly's own hosts both shapes render through one frame, AppSlugShell, so an
 * old address moving to its clean one keeps the shell and the page mounted.
 * On an agency's white-label host (isAgencyMode) nothing changes: no slug
 * route is declared and the old routes render exactly what they always did.
 * React Router ranks the static /app/account, /app/billing, /app/support,
 * /app/connections, /app/workspace and /app/l/... above :slug, and the
 * reserved words (lib/appSlugRules.js) make sure a slug never equals one.
 *
 * The home's main views have their own path word on Fly's own hosts (PR 1c;
 * Shardul, 23 Oct 26): /app/:slug/inbox, /agent, /activity, /growth and
 * /settings, and the same under /app/l/:locationId/ while an old address waits
 * to move. Each renders the home itself, so moving between views keeps the
 * page mounted; the home's router (useHomeRoute) moves an older ?section= into
 * the path. An agency's host declares none of them.
 *
 * Pages are written in the owner's words on Fly's own hosts since PR 6
 * (Shardul, 23 Oct 26; lib/appAddress.ts APP_PAGE_WORDS): /app/:slug/posts,
 * /posts/new, /growth/local-rank, /profile/history, /customers, /alerts,
 * /plan and the rest, and the same under /app/l/:locationId/. Each renders the
 * page its words name. Every old word stays declared under both shapes and
 * renders its page as before; the frame moves it to its new words with the
 * query and hash kept (AppSlugShell). An agency's host declares no word route.
 */
import { Fragment, lazy, Suspense, type ReactElement, type ReactNode } from 'react';
import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import { FunnelLoader } from '@/components/agent-shell/FunnelBoundary';
import { APP_HOME_SECTION_WORDS, APP_PAGE_WORD_ROUTES, appLocationIdFromPath, isAppSlug } from '@/lib/appAddress';
import { isAgencyMode } from '@/utils/agencyRouting';
import NotFound from '@/pages/NotFound';
import { AppLocationShell } from './AppLocationShell';
import { AppSlugShell } from './AppSlugShell';
import { appAreaRoutes } from '../appAreaRoutes';
import { APP_LOCATION_PAGES } from './pages';

const PublicAgentHomePage = lazy(() => import('@/pages/agent/PublicAgentHomePage'));
const PublicActivitiesPage = lazy(() => import('@/pages/agent/PublicActivitiesPage'));
const PublicNotificationsPage = lazy(() => import('@/pages/agent/PublicNotificationsPage'));
const PublicKeywordsPage = lazy(() => import('@/pages/agent/PublicKeywordsPage'));
const PublicPhotosPage = lazy(() => import('@/pages/agent/PublicPhotosPage'));
const PublicProfilePage = lazy(() => import('@/pages/agent/PublicProfilePage'));
const PublicProfileChangesPage = lazy(() => import('@/pages/agent/PublicProfileChangesPage'));
const PublicGBPServicesPage = lazy(() => import('@/pages/agent/PublicGBPServicesPage'));
const PublicGBPAttributesPage = lazy(() => import('@/pages/agent/PublicGBPAttributesPage'));
const PublicGBPCategoriesPage = lazy(() => import('@/pages/agent/PublicGBPCategoriesPage'));
const PublicWebsitePage = lazy(() => import('@/pages/agent/PublicWebsitePage'));
const PublicListingsPage = lazy(() => import('@/pages/agent/PublicListingsPage'));
const PublicPerformancePage = lazy(() => import('@/pages/agent/PublicPerformancePage'));
const PublicLeadsPage = lazy(() => import('@/pages/agent/PublicLeadsPage'));
const PublicContactsPage = lazy(() => import('@/pages/agent/PublicContactsPage'));
const PublicIdeasPage = lazy(() => import('@/pages/agent/PublicIdeasPage'));
const PublicCreatePostPage = lazy(() => import('@/pages/agent/PublicCreatePostPage'));
const PublicTemplatesPage = lazy(() => import('@/pages/agent/PublicTemplatesPage'));
const PublicReferencesPage = lazy(() => import('@/pages/agent/PublicReferencesPage'));
const PublicOptimizePage = lazy(() => import('@/pages/agent/PublicOptimizePage'));
const PublicDashboardPage = lazy(() => import('@/pages/agent/PublicDashboardPage'));
const PublicReviewGeneratorPage = lazy(() => import('@/pages/agent/PublicReviewGeneratorPage'));
const PublicReviewResponderPage = lazy(() => import('@/pages/agent/PublicReviewResponderPage'));
const PublicMicrositeSetupPage = lazy(() => import('@/pages/agent/PublicMicrositeSetupPage'));
const PublicOfferPage = lazy(() => import('@/pages/agent/PublicOfferPage'));
const PublicContentSettingsPage = lazy(() => import('@/pages/agent/PublicContentSettingsPage'));
const PublicSubscriptionsPage = lazy(() => import('@/pages/agent/PublicSubscriptionsPage'));
const AgentSubscriptionPage = lazy(() => import('@/pages/agent/AgentSubscriptionPage'));
const PublicWebsiteDomainPage = lazy(() => import('@/pages/agent/PublicWebsiteDomainPage'));
const PublicReportsPage = lazy(() => import('@/pages/agent/PublicReportsPage'));
const PublicBlogsPage = lazy(() => import('@/pages/agent/PublicBlogsPage'));

/** page segment -> the component the token route renders for it. */
const PAGE_ELEMENTS: Record<string, ReactElement> = {
  home: <PublicAgentHomePage />,
  activities: <PublicActivitiesPage />,
  notifications: <PublicNotificationsPage />,
  keywords: <PublicKeywordsPage />,
  photos: <PublicPhotosPage />,
  profile: <PublicProfilePage />,
  'profile-changes': <PublicProfileChangesPage />,
  'gbp-services': <PublicGBPServicesPage />,
  'gbp-attributes': <PublicGBPAttributesPage />,
  'gbp-categories': <PublicGBPCategoriesPage />,
  website: <PublicWebsitePage />,
  listings: <PublicListingsPage />,
  performance: <PublicPerformancePage />,
  leads: <PublicLeadsPage />,
  contacts: <PublicContactsPage />,
  ideas: <PublicIdeasPage />,
  createpost: <PublicCreatePostPage />,
  // The address /agent/new-post still renders the same page for links minted
  // before 18 Sep 26; the app keeps the pair so a rewritten link lands.
  'new-post': <PublicCreatePostPage />,
  templates: <PublicTemplatesPage />,
  references: <PublicReferencesPage />,
  'audit-report': <PublicOptimizePage />,
  dashboard: <PublicDashboardPage />,
  'review-generator-setup': <PublicReviewGeneratorPage />,
  'review-responder-setup': <PublicReviewResponderPage />,
  'microsite-setup': <PublicMicrositeSetupPage />,
  offer: <PublicOfferPage />,
  subscriptions: <PublicSubscriptionsPage />,
  subscription: <AgentSubscriptionPage />,
  'content-settings': <PublicContentSettingsPage />,
  reports: <PublicReportsPage />,
  blogs: <PublicBlogsPage />,
};

/**
 * One app page: its own boundary with the app's colour while its chunk loads,
 * and keyed by the location so a switch from the shell remounts it cleanly
 * (redesign, DESIGN.md 5, S). The same element type on every app route, so
 * React keeps the boundary across page changes and a navigation made in a
 * transition leaves the current page up until the next one is ready.
 *
 * Keyed by the location the address resolves to, either shape, so the move
 * from /app/l/<id>/... to /app/<slug>/... is the same key and the page stays.
 */
function LocationPage({ children }: { children: ReactNode }) {
  const { locationId, slug } = useParams<{ locationId?: string; slug?: string }>();
  const { pathname } = useLocation();
  const key = (appLocationIdFromPath(pathname) ?? locationId ?? slug ?? '').toLowerCase();
  return (
    <Suspense fallback={<FunnelLoader />}>
      <Fragment key={key}>{children}</Fragment>
    </Suspense>
  );
}

/**
 * The frame a location page sits in: AppSlugShell on Fly's own hosts (both
 * shapes, so a move keeps the page), today's AppLocationShell on an agency's
 * host.
 */
function framed(page: ReactElement, agencyHost: boolean): ReactElement {
  if (agencyHost) return <AppLocationShell><LocationPage>{page}</LocationPage></AppLocationShell>;
  return <AppSlugShell><LocationPage>{page}</LocationPage></AppSlugShell>;
}

/**
 * Pages that sit under another page, with both their addresses here so
 * App.tsx (past a thousand lines) needs no new line. The token stays where
 * every agent page keeps it, /agent/<page>/:token/..., so card links are
 * rewritten into the app the usual way (agentLinks.ts rewriteLinkForApp).
 *
 *   website/domain: the own-domain DNS steps (Anand, 25 Sep 26)
 *
 * And the Blogs page's token address (Shardul, 27 Oct 26), here for the same
 * reason; its /app twin comes from APP_LOCATION_PAGES like every page's.
 */
function subPageRoutes(agencyHost: boolean): ReactElement[] {
  return [
    <Route key="token:blogs" path="/agent/blogs/:token" element={<PublicBlogsPage />} />,
    <Route key="token:website/domain" path="/agent/website/:token/domain" element={<PublicWebsiteDomainPage />} />,
    <Route
      key="app:website/domain"
      path="/app/l/:locationId/website/domain"
      element={framed(<PublicWebsiteDomainPage />, agencyHost)}
    />,
  ];
}

/**
 * The bare location address, /app/l/:locationId, is the home. Every agent
 * WhatsApp and email links here (Anand, 26 Sep 26: "keep them all relevant
 * link only from /app page"), so it opens the home with the query and anchor
 * it came with (?card=, ?view=ops, ?draft=, #next-moves). The home route then
 * asks a signed-out owner to sign in and brings them back.
 */
export function AppLocationHomeRedirect() {
  const { locationId } = useParams<{ locationId: string }>();
  const { search, hash } = useLocation();
  if (!locationId) return <Navigate to="/app" replace />;
  return <Navigate to={{ pathname: `/app/l/${locationId}/home`, search, hash }} replace />;
}

/**
 * The bare clean address, /app/:slug, is the home, with the query and anchor
 * it came with. A reserved word or a malformed segment is not a location.
 */
export function AppSlugHomeRedirect() {
  const { slug } = useParams<{ slug: string }>();
  const { search, hash } = useLocation();
  const lower = String(slug ?? '').toLowerCase();
  if (!isAppSlug(lower)) return <NotFound />;
  return <Navigate to={{ pathname: `/app/${lower}/home`, search, hash }} replace />;
}

/** The clean twins of every location route, Fly's own hosts only. */
function slugRoutes(): ReactElement[] {
  return [
    <Route key="slug:location-home" path="/app/:slug" element={<AppSlugHomeRedirect />} />,
    ...APP_LOCATION_PAGES.map((page) => (
      <Route key={`slug:${page}`} path={`/app/:slug/${page}`} element={framed(PAGE_ELEMENTS[page], false)} />
    )),
    <Route key="slug:website/domain" path="/app/:slug/website/domain" element={framed(<PublicWebsiteDomainPage />, false)} />,
  ];
}

/**
 * The home's views at their own address, both shapes, Fly's own hosts only:
 * the home's element, the same one /home renders, so a move between views is
 * a change of address and nothing remounts.
 */
function homeViewRoutes(): ReactElement[] {
  return APP_HOME_SECTION_WORDS.flatMap((word) => [
    <Route key={`app:view:${word}`} path={`/app/l/:locationId/${word}`} element={framed(PAGE_ELEMENTS.home, false)} />,
    <Route key={`slug:view:${word}`} path={`/app/:slug/${word}`} element={framed(PAGE_ELEMENTS.home, false)} />,
  ]);
}

/**
 * The pages at their words (PR 6), both shapes, Fly's own hosts only. Words a
 * page shares with its key (home, profile, website, listings, reports) are
 * declared once, with the old words above.
 */
function pageWordRoutes(): ReactElement[] {
  const keys = new Set<string>(APP_LOCATION_PAGES);
  return APP_PAGE_WORD_ROUTES.filter(({ words }) => !keys.has(words)).flatMap(({ words, page }) => [
    <Route key={`app:words:${words}`} path={`/app/l/:locationId/${words}`} element={framed(PAGE_ELEMENTS[page], false)} />,
    <Route key={`slug:words:${words}`} path={`/app/:slug/${words}`} element={framed(PAGE_ELEMENTS[page], false)} />,
  ]);
}

/** The routes, for App.tsx to spread inside its own <Routes>. */
export function appLocationRoutes(): ReactElement[] {
  const agencyHost = isAgencyMode();
  return [
    <Route key="app:location-home" path="/app/l/:locationId" element={<AppLocationHomeRedirect />} />,
    ...APP_LOCATION_PAGES.map((page) => (
      <Route key={page} path={`/app/l/:locationId/${page}`} element={framed(PAGE_ELEMENTS[page], agencyHost)} />
    )),
    ...subPageRoutes(agencyHost),
    ...(agencyHost ? [] : slugRoutes()),
    ...(agencyHost ? [] : homeViewRoutes()),
    ...(agencyHost ? [] : pageWordRoutes()),
    ...appAreaRoutes(),
  ];
}
